import React, { useEffect, useState, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { toSlug, fmt, CATEGORY_STYLE, DEFAULT_STYLE } from '../api/services.api';
import { useCategories, useServices, useServiceSearch, toArray } from '../hooks/useCatalog';
import { useCartStore } from '../../cart/store/cartStore';
import { useToast } from '../../../shared/components/Toast/ToastProvider';
import Header from '../../../shared/components/Header';
import Footer from '../../../shared/components/Footer';

const BRAND = '#082189';

/* ─── Keyframes ─── */
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

/* ─── Global scroll reset ─── */
const GlobalScrollFix = createGlobalStyle`
  html, body {
    overflow-x: hidden !important;
    overflow-y: auto !important;
    height: auto !important;
  }
`;

/* ─── Styled components ─── */
const Scroll = styled.div`
  display: flex;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  @media (min-width: 768px) {
    display: grid !important;
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 20px !important;
    overflow: visible !important;
  }
  @media (min-width: 992px) {
    grid-template-columns: repeat(4, 1fr) !important;
  }
`;

const Cell = styled.div`
  flex: 0 0 280px;
  min-width: 280px;
  ${p => p.$delay != null && `animation: ${slideUp.getName()} 0.32s ${p.$delay}s both;`}

  @media (min-width: 768px) {
    flex: unset !important;
    min-width: unset !important;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.22s cubic-bezier(0.16,1,0.3,1), box-shadow 0.22s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 14px 36px rgba(0,0,0,0.11) !important;
  }
`;

const CardPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s cubic-bezier(0.16,1,0.3,1);

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const ViewLink = styled.span`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.1px;
`;

const Skel = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s infinite;
  border-radius: 8px;
`;

const MobileHint = styled.p`
  @media (min-width: 768px) {
    display: none !important;
  }
`;

const WhyCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const Page = styled.div`
  background: #f3f4f6;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  min-height: 100vh;
`;

const HeaderBar = styled.div`
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
`;

const AccentStrip = styled.div`
  height: 3px;
  background: linear-gradient(90deg, ${p => p.$from}, ${p => p.$to});
`;

const Wrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const Crumb = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  padding: 0;
`;

const CrumbSep = styled.span`
  color: #d1d5db;
  font-size: 13px;
`;

const CrumbCurrent = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
`;

const IconBox = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 14px;
  flex-shrink: 0;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
`;

const PageH1 = styled.h1`
  margin: 0 0 4px;
  font-size: clamp(20px, 4vw, 28px);
  font-weight: 900;
  color: #111827;
  letter-spacing: -0.4px;
  line-height: 1.15;
`;

const PageSub = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
`;

const StatsBar = styled.div`
  display: flex;
  align-items: stretch;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const StatCell = styled.div`
  flex: 1;
  min-width: 0;
  padding: 12px 16px;
  text-align: center;
  border-right: ${p => (p.$last ? 'none' : '1px solid #e5e7eb')};

  @media (max-width: 480px) {
    padding: 10px 6px;
  }
`;

const StatValue = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: ${p => p.$color};
  line-height: 1;

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardsWrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 24px 0 60px;
`;

const SectionTitleRow = styled.div`
  padding: 0 20px 18px;
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
`;

const SectionH2 = styled.h2`
  margin: 0;
  font-size: 19px;
  font-weight: 800;
  color: #111827;
`;

const SectionCount = styled.span`
  font-size: 13px;
  color: #9ca3af;
  font-weight: 500;
`;

const Empty = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 40px;
  margin-bottom: 12px;
`;

const EmptyTitle = styled.p`
  font-weight: 700;
  font-size: 18px;
  color: #111827;
  margin-bottom: 6px;
`;

const EmptyText = styled.p`
  font-size: 14px;
`;

const TrustSection = styled.div`
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 48px 20px;
`;

const TrustH3 = styled.h3`
  margin: 0 0 6px;
  text-align: center;
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.3px;
`;

const TrustSub = styled.p`
  margin: 0 0 28px;
  text-align: center;
  font-size: 14px;
  color: #6b7280;
`;

const TrustGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const WhyIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const WhyTitle = styled.p`
  margin: 0 0 3px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
`;

const WhyBody = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.55;
`;

const ErrorWrap = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const ErrorIcon = styled.span`
  font-size: 40px;
`;

const ErrorTitle = styled.p`
  font-weight: 800;
  font-size: 22px;
  color: #111827;
  margin: 0;
`;

const ErrorMsg = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const RetryBtn = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  background: #111827;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  border: none;
  cursor: pointer;
`;

/* ─── Search styled components ─── */
const SearchWrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px 20px 0;
`;

const SearchBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 16px;
  font-size: 16px;
  color: #9ca3af;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 13px 55px 13px 42px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  font-size: 14px;
  color: #111827;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s;

  &:focus {
    border-color: ${BRAND};
    box-shadow: 0 0 0 3px rgba(8,33,137,0.12);
  }
`;

const ResultsPanel = styled.div`
  margin-top: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.08);
  overflow: hidden;
  animation: ${slideUp} 0.28s both;
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }

  @media (max-width: 480px) {
    gap: 10px;
    padding: 12px;
  }
`;

const ResultImg = styled.img`
  width: 54px;
  height: 54px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
  background: #f3f4f6;
`;

const ResultInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ResultName = styled.p`
  margin: 0 0 3px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ResultMeta = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: #6b7280;
`;

const ResultPrice = styled.span`
  font-weight: 800;
  color: ${BRAND};
  margin-left: 10px;
`;

const AddBtn = styled.button`
  flex-shrink: 0;
  border: 1.5px solid ${BRAND};
  background: #ffffff;
  color: ${BRAND};
  font-size: 13px;
  font-weight: 700;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  transition: background 0.16s, color 0.16s, transform 0.14s;

  &:hover {
    background: ${BRAND};
    color: #ffffff;
    transform: scale(1.03);
  }
`;

const NoResults = styled.p`
  margin: 12px 0 0;
  padding: 0 4px;
  font-size: 13.5px;
  color: #6b7280;
`;

const SearchResults = ({ query, subCategoryID, subCategoryName }) => {
  const key = query.trim();
  const { data, isLoading } = useServiceSearch(key, subCategoryID);
  const results = toArray(data);
  const addItem = useCartStore((s) => s.addItem);
  const toast = useToast();

  if (key.length < 2) return null;

  const handleAdd = (r) => {
    addItem({
      subServiceID: r.subServiceID,
      subServiceName: r.subServiceName,
      quantity: 1,
      price: r.discountedPrice,
      cgst: r.cgst,
      sgst: r.sgst,
      imageURL: r.imageURL,
      serviceID: null,
      subCategoryID,
      categoryName: subCategoryName,
      categorySlug: toSlug(subCategoryName || ''),
      packageID: r.packageID,
    });
    toast.success('Added to cart');
  };

  if (!isLoading && results.length === 0) {
    return <NoResults>No results for “{key}”.</NoResults>;
  }

  if (results.length === 0) return null;

  return (
    <ResultsPanel>
      {results.map((r, i) => (
        <ResultRow key={r.subServiceID ?? i}>
          <ResultImg
            src={r.imageURL}
            alt={r.subServiceName}
            onError={(e) => { e.target.style.visibility = 'hidden'; }}
          />
          <ResultInfo>
            <ResultName>{r.subServiceName}</ResultName>
            <ResultMeta>
              {r.duration ? `${r.duration} · ` : ''}
              <ResultPrice>
                {fmt((r.discountedPrice || 0) + (r.cgst || 0) + (r.sgst || 0))}
              </ResultPrice>
            </ResultMeta>
          </ResultInfo>
          <AddBtn onClick={() => handleAdd(r)}>＋ Add</AddBtn>
        </ResultRow>
      ))}
    </ResultsPanel>
  );
};

const DEFAULT_IMG = 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=500&h=340&fit=crop';

const ServiceCard = memo(({ svc, index, accent, categorySlug, subCategoryID }) => {
  const navigate = useNavigate();

  const goToDetail = (pkgID) =>
    navigate(`/${categorySlug}/${subCategoryID}/${svc.serviceID}?pkg=${pkgID}`);

  const firstPkg = svc.packages?.[0];

  return (
    <Cell
      $delay={index * 0.05}
      onClick={() => firstPkg && goToDetail(firstPkg.packageID)}
    >
      <Card>
        {/* Service image */}
        <div style={{ height: 184, overflow: 'hidden', flexShrink: 0 }}>
          <CardPhoto
            src={svc.imageURL || svc.bannerURL || DEFAULT_IMG}
            alt={svc.serviceName}
            onError={e => { e.target.src = DEFAULT_IMG; }}
          />
        </div>

        {/* Card body */}
        <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 800, color: '#111827', lineHeight: 1.3 }}>
            {svc.serviceName}
          </p>
          {svc.description ? (
            <p style={{ margin: '0 0 10px', fontSize: 12.5, color: '#6b7280', lineHeight: 1.6, flex: 1 }}>
              {svc.description}
            </p>
          ) : null}

          <ViewLink style={{ marginTop: 'auto', paddingTop: 10, color: accent }}>
            View details →
          </ViewLink>
        </div>
      </Card>
    </Cell>
  );
});

const SkeletonCard = () => (
  <Cell>
    <Card style={{ pointerEvents: 'none', cursor: 'default' }}>
      <Skel style={{ height: 184 }} />
      <div style={{ padding: '14px 16px 16px' }}>
        <Skel style={{ height: 16, marginBottom: 8 }} />
        <Skel style={{ height: 12, width: '70%', marginBottom: 24 }} />
        <div style={{ display: 'flex', gap: 8 }}>
          <Skel style={{ height: 32, width: 90, borderRadius: 20 }} />
        </div>
      </div>
    </Card>
  </Cell>
);

const ServicesPage = () => {
  const { category, subCategoryID } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { data: catsData, isLoading: catsLoading, error: catsError } = useCategories();
  const { data: svcData, isLoading: svcLoading, error: svcError } = useServices(subCategoryID);

  const cats = toArray(catsData);
  // GetServices returns { subCategoryID, subCategoryName, services: [] } directly.
  const services = toArray(svcData);
  const apiSubCatName = svcData?.subCategoryName ?? null;
  const loading = catsLoading || svcLoading;
  const error = catsError?.message ?? svcError?.message ?? null;

  useEffect(() => {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    document.body.style.height = '';
    document.documentElement.style.height = '';
    window.scrollTo({ top: 0 });
    setQuery('');
  }, [subCategoryID]);

  const style = CATEGORY_STYLE[category] || DEFAULT_STYLE;
  const catData = cats.find(c => toSlug(c.categoryName ?? c.name ?? '') === category);
  const catName = catData?.categoryName ?? catData?.name ?? category.replace(/-/g, ' ');
  const subCatName = apiSubCatName
    ?? (catData?.subCategories ?? catData?.subcategories ?? [])
        .find(s => String(s.subCategoryID) === String(subCategoryID))?.subCategoryName
    ?? 'Services';

  if (error) {
    return (
      <>
        <GlobalScrollFix />
        <Header />
        <ErrorWrap>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Failed to load services</ErrorTitle>
          <ErrorMsg>{error}</ErrorMsg>
          <RetryBtn onClick={() => window.location.reload()}>
            Try Again
          </RetryBtn>
        </ErrorWrap>
        <Footer />
      </>
    );
  }

  return (
    <>
      <GlobalScrollFix />
      <Header />

      <Page>

        {/* Page header */}
        <HeaderBar>
          <AccentStrip $from={style.from} $to={style.to} />

          <Wrap style={{ padding: '24px 20px 28px' }}>
            {/* Breadcrumb */}
            <Breadcrumb>
              <Crumb onClick={() => navigate('/')}>
                Home
              </Crumb>
              <CrumbSep>›</CrumbSep>
              <Crumb onClick={() => navigate(`/${category}`)}>
                {catName}
              </Crumb>
              <CrumbSep>›</CrumbSep>
              <CrumbCurrent>{subCatName}</CrumbCurrent>
            </Breadcrumb>

            {/* Title row */}
            <TitleRow>
              <IconBox>
                {style.icon}
              </IconBox>
              <div>
                <PageH1>
                  {subCatName}
                </PageH1>
                <PageSub>
                  {catName} · Professional services at your doorstep
                </PageSub>
              </div>
            </TitleRow>

            {/* Stats bar */}
            <StatsBar>
              {[
                { value: '★ 4.9',           label: 'Rating',       valueColor: '#111827' },
                { value: loading ? '–' : `${services.length}`, label: 'Services', valueColor: '#111827' },
                { value: '✓ Guaranteed',     label: 'Satisfaction', valueColor: '#15803d' },
              ].map((stat, i, arr) => (
                <StatCell key={i} $last={i === arr.length - 1}>
                  <StatValue $color={stat.valueColor}>{stat.value}</StatValue>
                  <StatLabel>{stat.label}</StatLabel>
                </StatCell>
              ))}
            </StatsBar>
          </Wrap>
        </HeaderBar>

        {/* Search within this sub-category */}
        <SearchWrap>
          <SearchBox>
            <SearchIcon>🔍</SearchIcon>
            <SearchInput
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search in ${subCatName}…`}
              aria-label="Search services"
            />
          </SearchBox>
          <SearchResults
            query={query}
            subCategoryID={subCategoryID}
            subCategoryName={subCatName}
          />
        </SearchWrap>

        {/* Service cards */}
        <CardsWrap>
          <SectionTitleRow>
            <SectionH2>
              {subCatName}
            </SectionH2>
            {!loading ? (
              <SectionCount>
                {services.length} service{services.length !== 1 ? 's' : ''}
              </SectionCount>
            ) : null}
          </SectionTitleRow>

          <Scroll style={{ gap: 16, padding: '4px 20px 12px', alignItems: 'stretch' }}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
              : services.map((svc, i) => (
                  <ServiceCard
                    key={svc.serviceID ?? i}
                    svc={svc}
                    index={i}
                    accent={style.from}
                    categorySlug={category}
                    subCategoryID={subCategoryID}
                  />
                ))
            }
          </Scroll>

          {!loading && services.length === 0 ? (
            <Empty>
              <EmptyIcon>🔍</EmptyIcon>
              <EmptyTitle>No services found</EmptyTitle>
              <EmptyText>Check back soon — we're adding more services.</EmptyText>
            </Empty>
          ) : null}

          <MobileHint style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', margin: '4px 0 0' }}>
            swipe to see more →
          </MobileHint>
        </CardsWrap>

        {/* Trust section */}
        <TrustSection>
          <Wrap>
            <TrustH3>
              Why City Craft?
            </TrustH3>
            {/* <TrustSub>
              Trusted by 12M+ customers across India
            </TrustSub> */}
            <TrustGrid>
              {[
                { emoji: '🛡️', title: 'Verified Experts',    body: 'Background-checked & trained professionals' },
                { emoji: '💳', title: 'Transparent Pricing', body: 'No hidden charges. What you see is what you pay' },
                { emoji: '⭐', title: '4.8★ Rated',          body: 'Consistently top-rated by millions of customers' },
                // { emoji: '🔄', title: 'Free Re-service',     body: "Not happy? We come back and fix it at no charge" },
              ].map((item, i) => (
                <WhyCard key={i}>
                  <WhyIcon>
                    {item.emoji}
                  </WhyIcon>
                  <div>
                    <WhyTitle>{item.title}</WhyTitle>
                    <WhyBody>{item.body}</WhyBody>
                  </div>
                </WhyCard>
              ))}
            </TrustGrid>
          </Wrap>
        </TrustSection>

        <Footer />
      </Page>
    </>
  );
};

export default ServicesPage;
